import Icon, { IconType } from "@edaz/edazkit/Icon";
import Tooltip from "@edaz/edazkit/Tooltip";
import type { ColumnDefinition } from "@frui.ts/dataviews/dist";
import { Check, CollectionCheck } from "@frui.ts/bootstrap";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import React from "react";
import { observer } from "mobx-react-lite";
import type DistributorRefundCandidateResponse from "entities/distributorRefundCandidateResponse";
import DistributorRefundCandidateItemStatus from "entities/distributorRefundCandidateItemStatus";
import { formatAmount, formatDate, formatDateTime, formatNumber, translateEnum } from "utils/localizations";
import type { SelectableListViewModel } from "viewModels/selectableListViewModel";
import DistributorRefundsViewModel from "viewModels/timeCharges/distributors/distributorRefunds/distributorRefundsViewModel";
import DistributorRefundsFilter from "./distributorRefundsFilter";
import DistributorsToolbar from "./distributorsToolbar";
import ListView from "../../../common/list";

type SelectableDistributorRefundsViewModel = SelectableListViewModel<
  DistributorRefundsViewModel,
  DistributorRefundCandidateResponse
>;

const DistributorRefundsView: ViewComponent<DistributorRefundsViewModel> = observer(({ vm }) => {
  const columns: ColumnDefinition<DistributorRefundCandidateResponse>[] = [
    {
      title: (
        <Check
          custom
          target={vm as { allItems?: boolean }}
          property="allItems"
          threeState={true}
          disabled={!vm.isUserAllowedToEdit}
        />
      ),
      valueFormatter: props => {
        const status = props.item.status;
        const refundApproval = props.item.permittedActions.refundApproval;
        if (status !== DistributorRefundCandidateItemStatus.New) {
          let tooltip;
          let icon;

          if (status === DistributorRefundCandidateItemStatus.Approved) {
            icon = IconType.Info;
            tooltip = <span>{vm.translate("alreadyApproved")}</span>;
          } else {
            icon = IconType.CardPaymentYes;
            tooltip = <span>{vm.translate("alreadyProcessed")}</span>;
          }

          return (
            <Tooltip id={`help-${props.item.id}`} tooltip={tooltip}>
              <span style={{ cursor: "help" }}>
                <Icon icon={icon} width={20} height={20} />
              </span>
            </Tooltip>
          );
        } else if (!refundApproval.isAllowed) {
          const tooltip = (
            <span>{vm.translateEnum("distributorRefundCandidateApprovalRejectionReason", refundApproval.rejectionReason)}</span>
          );
          const icon = IconType.Cross;
          return (
            <Tooltip id={`help-${props.item.id}`} tooltip={tooltip}>
              <span style={{ cursor: "help" }}>
                <Icon icon={icon} width={20} height={20} className="text-danger" />
              </span>
            </Tooltip>
          );
        }

        return (
          <CollectionCheck
            custom
            target={vm}
            property="selectedItems"
            disabled={
              !vm.isUserAllowedToEdit ||
              props.item.status !== DistributorRefundCandidateItemStatus.New ||
              !refundApproval.isAllowed
            }
            value={props.item}
          />
        );
      },
    },
    {
      property: "confirmedAt",
      title: vm.translateAttribute("confirmedAt"),
      valueFormatter: props => formatDateTime(props.value),
    },
    {
      property: "priceListItemId",
      title: vm.translateAttribute("chargeTypeId"),
      valueFormatter: props => vm.translateChargeType(props.value),
    },
    {
      property: "orderIdent",
      title: vm.translateAttribute("orderIdent"),
    },
    {
      property: "amount",
      title: vm.translateAttribute("amount"),
      valueFormatter: props => formatAmount(props.value),
    },
    {
      property: "vehicle",
      title: vm.translateAttribute("vehicle"),
      valueFormatter: props => `${vm.translateCountry(props.value.countryId) ?? ""} - ${props.value.licensePlate}`,
    },
    {
      property: "chargeOrderId",
      title: vm.translateAttribute("chargeOrderId"),
    },
    {
      property: "salespointId",
      title: vm.translateAttribute("salespointId"),
    },
  ];

  const selectableVm = vm as SelectableDistributorRefundsViewModel;

  const aboveTable = (
    <>
      {vm.distributorRefundCandidates.length > 0 && (
        <div className="mb-4">
          {vm.distributorRefundCandidates.map(item => {
            const { distributorPaymentRequest, openRefundCandidateItemsCount } = item;
            return (
              <div
                key={distributorPaymentRequest.id}
                className="p-3 mb-2 rounded font-weight-normal border-0"
                style={{ backgroundColor: "#fff4e6" }}>
                <span
                  className="font-weight-bold mr-6 d-inline-block"
                  style={{ color: "#f37905", cursor: "pointer", width: "130px" }}
                  onClick={() => vm.onPaymentRequestClick(distributorPaymentRequest)}>
                  • {formatNumber(openRefundCandidateItemsCount)} {vm.translate("records", openRefundCandidateItemsCount)}
                </span>
                <span className="mr-6">{vm.translateDistributor(distributorPaymentRequest.distributorId)}</span>
                {distributorPaymentRequest.variableSymbol} - {formatDate(distributorPaymentRequest.validSinceDate)} -{" "}
                {translateEnum("distributorPaymentRequestType", distributorPaymentRequest.type)}
              </div>
            );
          })}
        </div>
      )}
      <DistributorRefundsFilter vm={vm} />
    </>
  );

  let underTable;
  if (vm.filterIsNotEmpty) {
    underTable = <DistributorsToolbar vm={vm} />;
  }

  return (
    <>
      <ListView
        columns={columns}
        vm={selectableVm}
        mandatoryFilter={true}
        aboveTable={aboveTable}
        underTable={underTable}
        rowProps={(item: DistributorRefundCandidateResponse, context) => {
          if (!item.permittedActions.refundApproval.isAllowed) {
            return { className: "table-danger" };
          }
          switch (item.status) {
            case DistributorRefundCandidateItemStatus.Approved:
              return { className: "table-warning" };
            case DistributorRefundCandidateItemStatus.Processed:
              return { className: "table-success" };
            default:
              return { className: "" };
          }
        }}
      />
    </>
  );
});

registerView(DistributorRefundsView, DistributorRefundsViewModel);
